/* Scrollable wrapper around the playfield. The middle region of the
   three-region app shell (.app__middle) is itself scrollable — this
   inner wrapper carries the .board-scroll class so the active row
   can be brought into view as the player advances downward.

   While the puzzle hasn't loaded, renders a dimensionally-consistent
   skeleton so there's no layout shift when the API call resolves. */

import { useEffect, useRef } from "preact/hooks";
import { cursor, guesses, loadError, puzzle } from "../game/state";
import { Board } from "./Board";
import { BoardSkeleton } from "./BoardSkeleton";
import { NoPuzzleToday } from "./NoPuzzleToday";

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

export function BoardScroll() {
  const wrap = useRef<HTMLDivElement | null>(null);

  // Read both signals so this component re-renders (and the effect
  // below re-runs) on every cursor move and after every committed
  // guess. The `void` discards keep tsc and eslint quiet.
  const _cursor = cursor.value;
  const _guessCount = guesses.value.length;
  void _cursor;
  void _guessCount;

  useEffect(() => {
    const root = wrap.current;
    if (root == null) return;
    const target = root.querySelector(
      "[data-current-row]",
    ) as HTMLElement | null;
    if (target == null) return;
    const reduced =
      typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia(REDUCED_MOTION).matches
        : false;
    // `center` keeps the active row as close to the middle of the
    // scrollport as possible; the browser clamps to top/bottom when
    // the target is closer to an edge than the scrollport's half-height.
    target.scrollIntoView({
      block: "center",
      behavior: reduced ? "auto" : "smooth",
    });
  });

  const err = loadError.value;
  return (
    <div class="board-scroll" ref={wrap}>
      {puzzle.value != null ? (
        <Board />
      ) : err != null ? (
        <NoPuzzleToday />
      ) : (
        <BoardSkeleton />
      )}
    </div>
  );
}
